// «Arma la venta»: equipo con «Cambiar por el kit» y los lugares de Promociones,
// extras como catálogo y resumen con los montos del servidor. Sin teléfono ni kit no pasa (REQ-80).
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, Tag } from 'lucide-react';
import { Button, ConfirmDialog, Loading } from '@/kit';
import {
    useCatalog,
    useReleaseProduct,
    useSelectProduct,
    useWizardSession,
    WIZARD_KEYS,
} from '../api/useWizard';
import {
    QUOTE_DISCARDED_MESSAGE,
    QUOTE_DISCARD_WARNING,
    quoteDiscarded,
} from '../quoteInvalidation';
import { canContinue, continueBlocker, descriptionsBySku } from '../cartLines';
import { continueGateLinesOf } from '../saleCharge';
import {
    anchorLineOf,
    extraCountsBySku,
    extraLinesOfSku,
    kitSwapsFor,
    kitSwapCapChange,
    latestExtraLineOf,
    saleSummaryOf,
    extraLinesOf,
} from '../buildSale';
import { needsCatalogReload } from '../lineErrors';
import { IntentKeys } from '../intentKeys';
import { isTransientRequestError } from '../deviceEnrollment';
import { earliestExpiringLineOf } from '../reservationClock';
import {
    hasExpiredPhone,
    hasLivePhone,
    hasLivePhoneNow,
    expiredExtraLinesOf,
    extraExpiredMessage,
    PHONE_EXPIRED_MESSAGE,
} from '../phoneReservation';
import { isReferredWithExtras } from '../externalReferral';
import {
    LINE_CHANGE_ACTION_LABEL,
    lineChangeAction,
    reservationErrorMessage,
    swapErrorMessage,
} from '../reservationErrors';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { BuildSaleEquipment, type KitSwapOption } from '../components/BuildSaleEquipment';
import { catalogForSession, isSureKeepConfirmed, summaryForSession } from '../sureKeepAvailability';
import { KitSwapNotice } from '../components/KitSwapNotice';
import { KitContentsModal } from '../components/KitContentsModal';
import { kitComponentCount, kitSourceOf } from '../kitContents';
import { BuildSaleSummaryBar, BuildSaleSummaryPanel } from '../components/BuildSaleSummary';
import { ExtrasCatalog } from '../components/ExtrasCatalog';
import { SaveAndExitButton, WizardBackLink } from '../components/WizardFootNav';
import { previousStepSlug, stepAfterCheckout } from '../steps';
import {
    anchorGiftLinesOf,
    saleQuoteStateOf,
    type AnchorGiftLine,
    type CardSaleBenefits,
    type OnSaleChanged,
    type SaleQuoteState,
    type SessionRefreshContext,
} from '../promotionSlots';
import type { CatalogItem, ReservedLine, SalePromotionRef, WizardSession } from '../types';
import type { LineSlots } from '../components/LineLabels';
import { pendingRevalidationLineIds } from '../promotionRevalidation';
import { LostLinesNotice } from '../components/LostLinesNotice';
import { lostLineViewsOf } from '../lostLines';
import { customerFirstName } from '../referrals';

/** Lo que recibe una pieza de Promociones montada junto al equipo. */
export interface AnchorSlotContext extends SessionRefreshContext {
    anchorLine: ReservedLine;
    /** Promo de venta principal de la sesión; ausente con «Ninguna». */
    salePromotion?: SalePromotionRef;
    /** Todas las promos de venta fijadas; en sesiones viejas falta. */
    salePromotions?: SalePromotionRef[];
    /** Líneas de extras de la venta, con sus promos. */
    extraLines?: ReservedLine[];
    /** Regalos vivos ligados a la ancla. */
    anchorGiftLines: AnchorGiftLine[];
    /** Con `charged` la promo, el regalo y el código ya no se cambian. */
    quoteState: SaleQuoteState;
    /** `true` si se puede escribir; con cotización viva antes pregunta si se descarta. */
    confirmQuoteDiscard: () => Promise<boolean>;
}

/** Bloquea «Continuar» mientras `pending`; `reason` se muestra bajo el botón. */
export type SetSelectionPending = (pending: boolean, reason?: string) => void;

export interface GiftSlotContext extends AnchorSlotContext {
    /** `true` mientras falte elegir regalo o la promo no esté confirmada (REQ-133). */
    setSelectionPending: SetSelectionPending;
}

export interface DiscountCodeSlotContext extends AnchorSlotContext {
    /** Vuelve a plegar el código. */
    close: () => void;
}

/** Lugares para las piezas de Promociones (REQ-134); sin pieza no dejan hueco ni bloquean. */
export interface BuildSaleSlots {
    /** Regalo junto al equipo. */
    gift?: (ctx: GiftSlotContext) => ReactNode;
    /** Promo de venta debajo del regalo: una por venta, con «Ninguna». */
    salePromotion?: (ctx: GiftSlotContext) => ReactNode;
    /** Código de descuento, plegado detrás de «¿El cliente trae un código de descuento?». */
    discountCode?: (ctx: DiscountCodeSlotContext) => ReactNode;
    /** Chip de promo en cada tarjeta de extra. */
    extraChip?: (item: CatalogItem) => ReactNode;
    /** Promos de los accesorios ya en la venta, arriba de los extras. */
    accessoryPromotions?: (ctx: AnchorSlotContext) => ReactNode;
    /** Banner verde de promos de categoría que tocan los extras, contadas sobre ellos. */
    extrasPromotionsBanner?: (items: readonly CatalogItem[]) => ReactNode;
    /** Burbujas propias y precio por unidad con descuento de cada extra. */
    extraBenefits?: (item: CatalogItem) => CardSaleBenefits;
    /** «Después en la venta»: la pieza pinta su encabezado y nada si no hay qué mostrar. */
    afterSale?: ReactNode;
    /** Nombre de un regalo en el resumen; sin pieza se usa el del catálogo. */
    giftName?: (gift: { sku: string; name: string }) => ReactNode;
    /** Nombre de la promo del equipo en el resumen; sin pieza, «Promoción». */
    promotionLabel?: LineSlots['promotionLabel'];
    /** Se llama después de agregar, quitar o cambiar líneas con éxito. */
    onSaleChanged?: OnSaleChanged;
    /** Lo que bajan las promos solo SureKeep; con él el resumen muestra «De contado $X». */
    sureKeepOnlyDiscountCents?: (session: WizardSession) => number;
    /** Aviso «Llegó recomendado»: aquí ya hay equipo y el evaluate trae el descuento del referido. */
    referredByNotice?: (customerName: string) => ReactNode;
}

const PHONE_CHECK_MS = 15_000;

/** Hora actual que se refresca cada `ms`, para notar un apartado que vence sin otra señal. */
function useNow(ms: number): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => {
            setNow(Date.now());
        }, ms);
        return () => {
            clearInterval(id);
        };
    }, [ms]);
    return now;
}

/** `busy` = otro apartado seguía en vuelo y este clic se ignora. */
type ReserveOutcome =
    | { status: 'reserved' }
    | { status: 'busy' }
    | { status: 'failed'; error: unknown };

export function BuildSaleStep({
    sessionId,
    slots = {},
}: {
    sessionId: string;
    slots?: BuildSaleSlots;
}) {
    const navigate = useNavigate();
    const { data: session, refresh: refreshSession } = useWizardSession(sessionId);
    const { data: rawCatalog, isLoading, isError, refetch } = useCatalog(sessionId);
    const sureKeep = isSureKeepConfirmed(session);
    const catalog = useMemo(() => catalogForSession(rawCatalog, sureKeep), [rawCatalog, sureKeep]);
    const selectProduct = useSelectProduct(sessionId);
    // Un solo apartado en vuelo: el doble clic en un extra o en el kit no aparta dos unidades.
    const reservingRef = useRef(false);
    const [intentKeys] = useState(() => new IntentKeys());
    const releaseProduct = useReleaseProduct(sessionId);
    const queryClient = useQueryClient();
    // Respuesta pendiente del aviso de descartar la cotización.
    const [discardPrompt, setDiscardPrompt] = useState<(ok: boolean) => void>();

    // El bloqueo (regalo o promo de venta sin su regalo) vale solo para la ancla que lo pidió.
    const [pendingBy, setPendingBy] = useState<{
        anchorId: string;
        gift?: string | true;
        sale?: string | true;
    }>();
    const anchor = session ? anchorLineOf(session) : undefined;
    const anchorId = anchor?.lineId;
    // Sin cambio real no toca el estado, así una pieza que avisa en cada render no hace bucle.
    const pendingSetter =
        (key: 'gift' | 'sale'): SetSelectionPending =>
        (pending, reason) => {
            if (anchorId === undefined) return;
            const value = pending ? (reason ?? true) : undefined;
            setPendingBy((prev) => {
                const base = prev?.anchorId === anchorId ? prev : { anchorId };
                return base === prev && base[key] === value ? prev : { ...base, [key]: value };
            });
        };
    const setSelectionPending = pendingSetter('gift');
    const setSalePending = pendingSetter('sale');
    const [codeOpen, setCodeOpen] = useState<boolean | undefined>(undefined);
    // Kit cuyo aviso está abierto; el aviso vale solo para la ancla que lo pidió.
    const [kitNotice, setKitNotice] = useState<{ anchorId: string; sku: string }>();
    const [kitContentsSku, setKitContentsSku] = useState<string>();
    // «Continuar» gira mientras carga la siguiente pantalla.
    const [leaving, setLeaving] = useState(false);

    const items = useMemo(() => catalog?.items ?? [], [catalog]);
    const descriptions = useMemo(() => descriptionsBySku(items), [items]);
    const now = useNow(PHONE_CHECK_MS);
    const phoneLive = session !== undefined && hasLivePhone(session, now);
    const phoneExpired = session !== undefined && hasExpiredPhone(session, now);
    // La venta derivada ya soltó el teléfono: no hay teléfono que vigilar.
    const cashOnly = session !== undefined && isReferredWithExtras(session);
    // Si el teléfono vence o se suelta con la pantalla abierta, se vuelve a elegirlo; los extras se quedan.
    const hadLivePhone = useRef(false);
    useEffect(() => {
        if (phoneLive) {
            hadLivePhone.current = true;
            return;
        }
        if (cashOnly || !(hadLivePhone.current || phoneExpired)) return;
        hadLivePhone.current = false;
        toast.warning(PHONE_EXPIRED_MESSAGE);
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'product' },
        });
    }, [phoneLive, phoneExpired, cashOnly, navigate, sessionId]);

    // Un extra vencido se quita de la venta con aviso; se suelta una sola vez por línea.
    const expiredExtras =
        session !== undefined && phoneLive ? expiredExtraLinesOf(session, now) : [];
    const expiredKey = expiredExtras.map((l) => l.lineId).join('|');
    const releasedExpired = useRef(new Set<string>());
    const releaseExpired = useRef<(lines: ReservedLine[]) => void>(() => undefined);
    useEffect(() => {
        releaseExpired.current = (lines) => {
            const fresh = lines.filter((l) => !releasedExpired.current.has(l.lineId));
            if (fresh.length === 0) return;
            for (const l of fresh) {
                releasedExpired.current.add(l.lineId);
                toast.warning(
                    extraExpiredMessage(
                        catalogDisplayName({ sku: l.sku, ...l.product }, descriptions),
                    ),
                );
            }
            void (async () => {
                for (const l of fresh) {
                    await releaseProduct.mutateAsync({ lineId: l.lineId }).catch(() => undefined);
                }
                void slots.onSaleChanged?.({ kind: 'lines' });
            })();
        };
    });
    useEffect(() => {
        if (expiredKey !== '') releaseExpired.current(expiredExtras);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- la llave resume las líneas vencidas
    }, [expiredKey]);

    if (!session) return <Loading size="lg" />;

    const summary = summaryForSession(saleSummaryOf(session, descriptions), sureKeep);
    // De contado no van las promos ni los códigos que solo aplican con SureKeep.
    const cashTotalCents = summary.totalCents + (slots.sureKeepOnlyDiscountCents?.(session) ?? 0);
    const kits: KitSwapOption[] = kitSwapsFor(items, anchor).map((kit) => ({
        sku: kit.sku,
        name: catalogDisplayName(kit, descriptions),
        priceCents: kit.cashPriceCents,
        ...(kit.affordableOnCredit !== undefined
            ? { affordableOnCredit: kit.affordableOnCredit }
            : {}),
        ...(kit.financeable !== undefined ? { financeable: kit.financeable } : {}),
        componentCount: kitComponentCount(kit),
    }));
    const kitInModal =
        kitContentsSku === undefined ? null : kitSourceOf(kitContentsSku, items, anchor);
    const swapKitInModal = kits.some((k) => k.sku === kitContentsSku);
    const firstLine = earliestExpiringLineOf(session);
    const firstToExpire =
        firstLine === undefined
            ? undefined
            : {
                  expiresAt: firstLine.expiresAt,
                  ...(firstLine.lineId !== anchor?.lineId && {
                      name: catalogDisplayName(
                          { sku: firstLine.sku, ...firstLine.product },
                          descriptions,
                      ),
                  }),
              };
    const counts = extraCountsBySku(session);
    const lines = continueGateLinesOf(session);
    const ownPending = pendingBy?.anchorId === anchorId ? pendingBy : undefined;
    const revalidating = pendingRevalidationLineIds(session).length > 0;
    const giftBlocks =
        revalidating || ownPending?.gift !== undefined || ownPending?.sale !== undefined;
    // Motivo bajo «Continuar» cuando lo frenan las promos.
    const slotReason = [ownPending?.gift, ownPending?.sale].find(
        (r): r is string => typeof r === 'string',
    );
    const promotionsReason = slotReason ?? (revalidating ? 'Confirmando promociones…' : undefined);
    // La venta derivada ya soltó el teléfono: solo queda cobrar sus extras.
    const referred = isReferredWithExtras(session);
    const ready = (cashOnly || phoneLive) && canContinue(lines) && !giftBlocks;
    const blocker = continueBlocker(lines);
    const busy = selectProduct.isPending || releaseProduct.isPending;

    // Promo, regalo o código con cotización viva: se descarta como al cambiar productos (DEC-48).
    function confirmQuoteDiscard(): Promise<boolean> {
        const state = session ? saleQuoteStateOf(session) : 'open';
        if (state === 'open') return Promise.resolve(true);
        if (state === 'charged') return Promise.resolve(false);
        return new Promise((resolve) => {
            setDiscardPrompt(() => resolve);
        });
    }
    const answerDiscard = (ok: boolean): void => {
        discardPrompt?.(ok);
        setDiscardPrompt(undefined);
    };

    // Una pieza que falla al recibir el aviso no rompe el paso.
    const notifyLinesChanged = (): void => {
        try {
            void slots.onSaleChanged?.({ kind: 'lines' });
        } catch {
            // La pieza de Promociones maneja sus propios errores.
        }
    };
    const slotContextOf = (slotLine: ReservedLine): AnchorSlotContext => ({
        anchorLine: slotLine,
        ...(session.salePromotion !== undefined && { salePromotion: session.salePromotion }),
        ...(session.salePromotions !== undefined && { salePromotions: session.salePromotions }),
        extraLines: extraLinesOf(session),
        anchorGiftLines: anchorGiftLinesOf(session),
        quoteState: saleQuoteStateOf(session),
        confirmQuoteDiscard,
        refreshSession: async (): Promise<void> => {
            const before = session;
            await refreshSession();
            const after = queryClient.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
            if (after && quoteDiscarded(before, after)) toast.info(QUOTE_DISCARDED_MESSAGE);
        },
    });
    const anchorContext = anchor && slotContextOf(anchor);

    /** Avisa el error con el botón de su acción; `retry` repite la misma operación. */
    const reportError = (
        error: unknown,
        retry: () => void,
        message = reservationErrorMessage(error),
    ): void => {
        const action = lineChangeAction(error);
        if (action === undefined) {
            toast.error(message);
        } else {
            const onClick =
                action === 'retry'
                    ? retry
                    : () => {
                          goToStep(action === 'delivery' ? stepAfterCheckout(session) : 'product');
                      };
            toast.error(message, { action: { label: LINE_CHANGE_ACTION_LABEL[action], onClick } });
        }
        // Un fallo puede dejar la línea PENDING o RELEASING: releer la sesión lo muestra.
        void refreshSession();
        if (needsCatalogReload(error)) void refetch();
    };

    /** Aparta con un solo apartado en vuelo; el reintento de la misma intención reusa la llave. */
    const reserveLine = async (sku: string, replacesLineId?: string): Promise<ReserveOutcome> => {
        if (reservingRef.current) return { status: 'busy' };
        reservingRef.current = true;
        const intent = `${sku}|${replacesLineId ?? ''}`;
        try {
            await selectProduct.mutateAsync({
                sku,
                qty: 1,
                ...(replacesLineId !== undefined ? { replacesLineId } : {}),
                idempotencyKey: intentKeys.keyFor(intent),
            });
            intentKeys.settle(intent);
            return { status: 'reserved' };
        } catch (error) {
            // Solo un fallo pasajero reusa la llave; un rechazo del silo deja intentar de nuevo.
            if (!isTransientRequestError(error)) intentKeys.settle(intent);
            return { status: 'failed', error };
        } finally {
            reservingRef.current = false;
        }
    };

    const addExtra = async (sku: string): Promise<void> => {
        const outcome = await reserveLine(sku);
        if (outcome.status === 'failed') reportError(outcome.error, () => void addExtra(sku));
        if (outcome.status !== 'reserved') return;
        notifyLinesChanged();
    };

    const removeExtra = async (sku: string): Promise<void> => {
        const latest = latestExtraLineOf(session, sku);
        if (latest === undefined) return;
        try {
            await releaseProduct.mutateAsync({ lineId: latest.lineId });
        } catch (error) {
            reportError(error, () => void removeExtra(sku));
            return;
        }
        notifyLinesChanged();
    };

    // «✕»: suelta una por una las líneas del SKU; no hay endpoint que las suelte juntas.
    const removeAllOfExtra = async (sku: string): Promise<void> => {
        try {
            for (const l of extraLinesOfSku(session, sku)) {
                await releaseProduct.mutateAsync({ lineId: l.lineId });
            }
        } catch (error) {
            reportError(error, () => void removeAllOfExtra(sku));
            return;
        }
        notifyLinesChanged();
    };

    // Aparta el kit y suelta la ancla con sus regalos en una sola llamada (REQ-84, REQ-132).
    const swapToKit = async (sku: string, replacesLineId: string): Promise<void> => {
        const outcome = await reserveLine(sku, replacesLineId);
        if (outcome.status === 'failed') {
            const { error } = outcome;
            reportError(error, () => void swapToKit(sku, replacesLineId), swapErrorMessage(error));
        }
        if (outcome.status !== 'reserved') return;
        setKitNotice(undefined);
        notifyLinesChanged();
    };

    // Pieza que Zentral se llevó: se suelta para elegir otra.
    const removeLost = async (lineId: string): Promise<void> => {
        try {
            await releaseProduct.mutateAsync({ lineId });
        } catch (error) {
            reportError(error, () => void removeLost(lineId));
            return;
        }
        notifyLinesChanged();
    };

    const noticeKit =
        kitNotice !== undefined && kitNotice.anchorId === anchorId
            ? kits.find((k) => k.sku === kitNotice.sku)
            : undefined;

    const goToStep = (
        step:
            | 'product'
            | 'payment-method'
            | 'quote'
            | 'referrals'
            | 'delivery'
            | 'payment'
            | 'welcome',
    ): void => {
        void navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step } });
    };

    const continueButton = (
        <Button
            disabled={!ready}
            isLoading={leaving}
            rightIcon={<ArrowRight className="size-4" aria-hidden />}
            onClick={() => {
                if (!cashOnly && !hasLivePhoneNow(session)) {
                    toast.warning(PHONE_EXPIRED_MESSAGE);
                    goToStep('product');
                    return;
                }
                setLeaving(true);
                void Promise.resolve(
                    navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: referred ? 'payment' : 'payment-method' },
                    }),
                ).finally(() => {
                    setLeaving(false);
                });
            }}
        >
            Continuar
        </Button>
    );

    // Por qué «Continuar» no avanza; se pinta junto al botón.
    const continueReason =
        blocker !== undefined ? (
            <p role="status" className="text-[12px] text-warning">
                {blocker}
            </p>
        ) : promotionsReason !== undefined ? (
            <p role="status" className="text-[12px] text-muted-foreground">
                {promotionsReason}
            </p>
        ) : null;

    const isCodeOpen = codeOpen ?? anchor?.discountCode !== undefined;

    // Código de descuento plegado; con teléfono va junto al equipo, sin él sobre la portadora.
    const discountCodeSlot = (ctx: AnchorSlotContext): ReactNode =>
        slots.discountCode &&
        (isCodeOpen ? (
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    <Tag className="size-3.5" aria-hidden />
                    Código de descuento
                </div>
                {slots.discountCode({
                    ...ctx,
                    close: () => {
                        setCodeOpen(false);
                    },
                })}
            </div>
        ) : (
            <button
                type="button"
                className="flex items-center gap-1.5 self-start text-[12px] font-medium text-brand-accent hover:underline"
                onClick={() => {
                    setCodeOpen(true);
                }}
            >
                <Tag className="size-3.5" aria-hidden />
                ¿El cliente trae un código de descuento?
            </button>
        ));

    return (
        <div className="flex w-full max-w-6xl flex-col pb-28 lg:pb-0">
            <header className="mb-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Selección
                    </div>
                    <h1 className="mb-1 text-[20px] font-semibold leading-[1.2] tracking-[-0.01em]">
                        Arma la venta
                    </h1>
                    <p className="text-[13px] leading-[1.5] text-muted-foreground">
                        Confirma el equipo y agrega extras.
                    </p>
                </div>
                <div className="flex flex-none items-center gap-4 pt-1">
                    {!referred && (
                        <WizardBackLink
                            sessionId={sessionId}
                            step={previousStepSlug('build-sale', session) ?? 'product'}
                        />
                    )}
                    <SaveAndExitButton />
                </div>
            </header>

            {slots.referredByNotice?.(customerFirstName(session))}

            <div className="mb-3 empty:hidden">
                <LostLinesNotice
                    lines={lostLineViewsOf(session, descriptions)}
                    onChoosePhone={() => {
                        goToStep('product');
                    }}
                    onRemove={(lineId) => void removeLost(lineId)}
                    {...(releaseProduct.isPending &&
                        'lineId' in releaseProduct.variables && {
                            removingLineId: releaseProduct.variables.lineId,
                        })}
                />
            </div>

            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
                <div className="flex min-w-0 flex-col gap-4">
                    {summary.equipment && anchor && anchorContext ? (
                        <BuildSaleEquipment
                            equipment={summary.equipment}
                            busy={busy}
                            onChange={
                                referred
                                    ? undefined
                                    : () => {
                                          goToStep('product');
                                      }
                            }
                            kits={kits}
                            firstToExpire={firstToExpire}
                            onSwapToKit={(sku) => {
                                setKitNotice({ anchorId: anchor.lineId, sku });
                            }}
                            onShowKitContents={setKitContentsSku}
                            notice={
                                noticeKit && (
                                    <KitSwapNotice
                                        kit={noticeKit}
                                        giftNames={summary.gifts.map((g) => g.name)}
                                        discountCode={anchor.discountCode}
                                        capChange={
                                            sureKeep
                                                ? kitSwapCapChange(session, noticeKit.priceCents)
                                                : undefined
                                        }
                                        creditCapCents={catalog?.creditCapCents}
                                        busy={busy}
                                        onConfirm={() =>
                                            void swapToKit(noticeKit.sku, anchor.lineId)
                                        }
                                        onCancel={() => {
                                            setKitNotice(undefined);
                                        }}
                                    />
                                )
                            }
                        >
                            {slots.gift && (
                                <div data-testid="slot-gift">
                                    {slots.gift({ ...anchorContext, setSelectionPending })}
                                </div>
                            )}
                            {slots.salePromotion && (
                                <div data-testid="slot-sale-promotion">
                                    {slots.salePromotion({
                                        ...anchorContext,
                                        setSelectionPending: setSalePending,
                                    })}
                                </div>
                            )}
                            {discountCodeSlot(anchorContext)}
                        </BuildSaleEquipment>
                    ) : (
                        <p role="status" className="text-[12px] text-muted-foreground">
                            {referred
                                ? 'La venta se derivó: solo cobras aquí los extras de contado.'
                                : 'Elige un teléfono del catálogo.'}
                        </p>
                    )}

                    {slots.accessoryPromotions && anchorContext && (
                        <div data-testid="slot-accessory-promotions">
                            {slots.accessoryPromotions(anchorContext)}
                        </div>
                    )}
                    <ExtrasCatalog
                        items={items}
                        counts={counts}
                        loading={isLoading}
                        error={isError}
                        onRetry={() => void refetch()}
                        onAdd={addExtra}
                        onRemove={removeExtra}
                        onRemoveAll={removeAllOfExtra}
                        chip={slots.extraChip}
                        banner={slots.extrasPromotionsBanner}
                        benefits={slots.extraBenefits}
                    />
                </div>

                <BuildSaleSummaryPanel
                    summary={summary}
                    cashTotalCents={cashTotalCents}
                    continueButton={continueButton}
                    continueReason={continueReason}
                    showFinancing={sureKeep}
                    afterSale={slots.afterSale}
                    giftName={slots.giftName}
                    promotionLabel={slots.promotionLabel}
                />
            </div>

            <BuildSaleSummaryBar
                summary={summary}
                continueButton={continueButton}
                continueReason={continueReason}
                cashTotalCents={cashTotalCents}
            />

            <KitContentsModal
                kit={kitInModal}
                items={items}
                onClose={() => {
                    setKitContentsSku(undefined);
                }}
                {...(swapKitInModal &&
                    anchor !== undefined &&
                    kitContentsSku !== undefined && {
                        chooseLabel: 'Cambiar por este kit',
                        busy,
                        onChoose: () => {
                            setKitNotice({ anchorId: anchor.lineId, sku: kitContentsSku });
                            setKitContentsSku(undefined);
                        },
                    })}
            />

            <ConfirmDialog
                open={discardPrompt !== undefined}
                onOpenChange={(open) => {
                    if (!open) answerDiscard(false);
                }}
                title="La venta ya está cotizada"
                description={QUOTE_DISCARD_WARNING}
                confirmLabel="Cambiar y volver a cotizar"
                cancelLabel="Mejor no"
                onConfirm={() => {
                    answerDiscard(true);
                }}
            />
        </div>
    );
}
